import FormInput from "@/components/forms/FormInput";
import FormSelect from "@/components/forms/FormSelect";
import FormSwitch from "@/components/forms/FormSwitch";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { useCreateUrlRedirectMutation } from "@/app/features/urlRedirect/urlRedirectApiSlice";

const pathSchema = Yup.string()
  .required()
  .trim()
  .max(500)
  .test(
    "valid-path",
    "Enter a site path such as /car-news/best-hybrid-suvs/",
    (value) => {
      if (!value) return false;
      if (value.startsWith("/")) return value.length > 1;
      try {
        const parsed = new URL(value);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch {
        return false;
      }
    }
  );

const validationSchema = Yup.object().shape({
  fromPath: pathSchema.label("From path"),
  toPath: pathSchema.label("To path"),
  statusCode: Yup.string().oneOf(["301", "302"]).required().label("Type"),
  isActive: Yup.boolean().required().label("Active"),
  note: Yup.string().max(500).label("Note"),
});

const typeItems = [
  { name: "301 (permanent)", code: "301" },
  { name: "302 (temporary)", code: "302" },
];

const initialValues = {
  fromPath: "",
  toPath: "",
  statusCode: "301",
  isActive: true,
  note: "",
};

export default function CreateRedirectPage() {
  const [createUrlRedirect, { isLoading }] = useCreateUrlRedirectMutation();
  const navigate = useNavigate();

  const handleSubmit = async (inputs) => {
    try {
      const result = await createUrlRedirect({
        fromPath: inputs.fromPath.trim(),
        toPath: inputs.toPath.trim(),
        statusCode: Number(inputs.statusCode),
        isActive: Boolean(inputs.isActive),
        note: inputs.note?.trim() || "",
      });

      if (result?.data) {
        toast.success(result.data.message);
        navigate("/redirects");
      } else if (result?.error) {
        toast.error(result.error?.data?.message || "Failed to create");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4">Create Redirect</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Example: /car-news/best-hybrid-suvs/ → /guides/best-hybrid-suvs/
            </Typography>
          </Box>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {() => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="From path"
                    name="fromPath"
                    type="text"
                    placeholder="/car-news/best-hybrid-suvs/"
                  />
                  <FormInput
                    label="To path"
                    name="toPath"
                    type="text"
                    placeholder="/guides/best-hybrid-suvs/"
                  />
                  <FormSelect
                    label="Type"
                    name="statusCode"
                    items={typeItems}
                  />
                  <FormSwitch label="Active" name="isActive" />
                  <FormInput
                    label="Note (optional)"
                    name="note"
                    type="text"
                    placeholder="Why this redirect exists"
                  />
                  <Button type="submit" variant="contained" disabled={isLoading}>
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
